import { dbs } from 'neuro-schemas';
import { isPartialDate, partialDateToValue, sortPartialDate } from 'neuro-utils';
import { equals, keys } from 'ramda';
import { isEmpty, omit } from 'Utility/ramdaReplacement';

import type { ILeadClickerType } from '../Document/Form/LeadClicker';
import type { IProgram } from 'neuro-schemas/dbs';

const {
  utils: {
    backwardsCompatibility,
    getLatestActiveDiscontinuation,
    getLatestDiscontinuation,
    getRechargeableGeneratorsByPlatform,
    getPrimaryCellGeneratorsByPlatform,
  },
} = dbs;

/**
 * Handle discontinuations and continuations in DBS form and set treatment status accordingly
 * @param {any} onChange - onChange function for updating document values
 * @param {IDBSDiscontinuationEvent[] | undefined} discontinuations - Array of discontinuation events in the document
 * @param {IDBSGenerator[] | undefined} generators - Array of generators in the document
 * @param {string} status - Current status of the treatment
 * @param {boolean} hasEnded - True if treatment has been marked as ended, false if not
 */
export const discontinuationHandler = (
  onChange: IFormData['onChange'],
  discontinuations: Array<IDBSDiscontinuationEvent> | undefined,
  generators: Array<IDBSGenerator> | undefined,
  status: string,
  hasEnded: boolean,
): void => {
  const newDiscontinuations = structuredClone(discontinuations ?? []); //JSON.parse(JSON.stringify(discontinuations ?? []));
  const removalDiscontinuations = newDiscontinuations.filter(
    (d: IDBSDiscontinuationEvent) => d.discontinuationType !== 'generatorSwitchedOff',
  );

  const newGenerators = structuredClone(generators ?? []); // JSON.parse(JSON.stringify(generators ?? []));
  const latestDiscontinuation = getLatestActiveDiscontinuation(newDiscontinuations);

  const updateDiscontinuationEndDates = (): void => {
    newGenerators.forEach((g: IDBSGenerator): void => {
      newDiscontinuations.forEach((d: IDBSDiscontinuationEvent, i: number): void => {
        if (
          d.discontinuationType !== 'generatorSwitchedOff' &&
          sortPartialDate(g.generatorDate, d.date) >= 0 &&
          newDiscontinuations[i]
        ) {
          newDiscontinuations[i] = { ...newDiscontinuations[i], endDate: g.generatorDate };
          return;
        }
      });
    });
  };

  const removeDiscontinuationEndDates = (): void => {
    removalDiscontinuations
      .filter(
        (d: IDBSDiscontinuationEvent) =>
          !newGenerators
            .slice(0, newGenerators.length - 1)
            .some((g: IDBSGenerator) => equals(d.endDate, g.generatorDate)),
      )
      .forEach((r: IDBSDiscontinuationEvent) => {
        const targetIndex = newDiscontinuations.indexOf(r);
        if (targetIndex >= 0 && newDiscontinuations[targetIndex]) {
          newDiscontinuations[targetIndex] = { ...newDiscontinuations[targetIndex], endDate: undefined };
        }
      });
  };

  if (hasEnded) {
    status !== 'ended' && onChange?.({ status: 'ended' });
  }

  if (!Array.isArray(newDiscontinuations) || newDiscontinuations.length < 1) {
    !hasEnded && status !== 'inProgress' && onChange?.({ status: 'inProgress' });
  }

  if (latestDiscontinuation) {
    if (latestDiscontinuation.discontinuationType === 'generatorSwitchedOff') {
      !hasEnded &&
        !isPartialDate(latestDiscontinuation.endDate) &&
        status !== 'suspended' &&
        onChange?.({ status: 'suspended' });
    } else {
      if (newGenerators.length < removalDiscontinuations.length + 1) {
        !hasEnded && status !== 'suspended' && onChange?.({ status: 'suspended' });
      }
      if (newGenerators.length >= removalDiscontinuations.length + 1) {
        updateDiscontinuationEndDates();
        removeDiscontinuationEndDates();

        !equals(newDiscontinuations, discontinuations) && onChange?.({ discontinuationEvents: newDiscontinuations });
      }
      if (removalDiscontinuations.find((d: IDBSDiscontinuationEvent) => !d.endDate)) {
        !hasEnded && status !== 'suspended' && onChange?.({ status: 'suspended' });
      }
    }
  } else {
    if (newGenerators.length >= removalDiscontinuations.length + 1) {
      !hasEnded && status !== 'inProgress' && onChange?.({ status: 'inProgress' });

      updateDiscontinuationEndDates();
      removeDiscontinuationEndDates();

      !equals(newDiscontinuations, discontinuations) && onChange?.({ discontinuationEvents: newDiscontinuations });
    } else {
      !hasEnded && status !== 'suspended' && onChange?.({ status: 'suspended' });

      removeDiscontinuationEndDates();

      !equals(newDiscontinuations, discontinuations) && onChange?.({ discontinuationEvents: newDiscontinuations });
    }
  }
};

/**
 * Manage generator settings based on actions/changes done to generator(s)
 * @param {IFormData} formData - formData that has DBS documents and onChange function for updating the values
 * @param {'generator_deleted' | 'generator_changed' | 'generator_date_changed'} action - type of action done to generator(s)
 * @param {IDBSGenerator} generator - the generator related to the change
 */
export const generatorSettingHandler = (
  formData: IFormData<IDocumentWithTypeV2<'dbs'>>,
  action: 'generator_deleted' | 'generator_changed' | 'generator_date_changed',
  generator?: IDBSGenerator,
): void => {
  const arraysAreValid = Array.isArray(formData.document.settings) && Array.isArray(formData.document.generators);
  switch (action) {
    case 'generator_deleted':
    case 'generator_changed': {
      if (arraysAreValid) {
        const newSettings = formData.document.settings?.filter(
          (setting: IDBSSetting) => setting.generator !== generator?.id,
        );
        formData.onChange?.({ settings: newSettings });
      }
      break;
    }
    default: {
      return;
    }
  }
};

/**
 * Merge lead settings from two programs
 * @param {IProgram} document - The program which the settings are merged to
 * @param {IProgram} referenceDocument - The program which the settings are merged from
 * @returns {IProgram} Program object with the merged settings
 */
export const mergeLeadSettings = (
  document: ILeadClickerType,
  referenceDocument: ILeadClickerType,
): ILeadClickerType => {
  const merged = {
    ...document,
    rightSettings: Object.keys(referenceDocument.rightSettings ?? {}).reduce(
      (current, key) => {
        if (!document.rightSettings || document.rightSettings[key]) {
          current[key] = referenceDocument.rightSettings?.[key];
        }
        return current;
      },
      { ...document.rightSettings, ...referenceDocument.rightSettings },
    ),
    leftSettings: Object.keys(referenceDocument.leftSettings ?? {}).reduce(
      (current, key) => {
        if (!document.leftSettings || document.leftSettings[key]) {
          current[key] = referenceDocument.leftSettings?.[key];
        }
        return current;
      },
      { ...document.leftSettings, ...referenceDocument.leftSettings },
    ),
  };
  return merged;
};

/**
 * Filter undefined fields in an object
 * @param {object} data - object
 * @returns {object} Data without undefined fields
 */
export const filterUndefinedFields = (data: Record<string, unknown>): IProgram['rightSettings' | 'leftSettings'] => {
  const keysOfUndefinedFields = keys(data).filter(key => data[key] === undefined);
  const filteredData = omit(keysOfUndefinedFields, data);
  return filteredData;
};

/**
 * Filter 'plus' and 'minus' fields in an object
 * @param {object} data - object
 * @returns {object} Data without undefined fields
 */
export const filterPlusMinusFields = <T extends Record<string, unknown>>(data: T): T => {
  const keysOfUndefinedFields = keys(data).filter(key => (['plus', 'minus'] as unknown[]).includes(data[key]));
  const filteredData = omit(keysOfUndefinedFields as string[], data);
  return filteredData as T;
};

/**
 * Sort generators by date and save the changes
 * @param {IOwnProps['formData']} formData - current formData
 */
export const sortByDateAndSave = (formData: IOwnProps['formData']): void => {
  const gens = formData.document.generators ?? [];
  const sortedGens = gens.toSorted((n1, n2) => sortPartialDate(n2.generatorDate, n1.generatorDate));
  formData.onChange && formData.onChange({ generators: sortedGens });
};

/**
 * Set DBS treatment status to 'inProgress' if it's not already 'inProgress' or 'ended'
 * @param {IOwnProps['formData']} formData - current formData
 */
export const setStatus = (formData: IOwnProps['formData']): void => {
  if (formData.document.status != 'inProgress' && formData.document.status != 'ended') {
    formData.onChange && formData.onChange({ status: 'inProgress' });
  }
};

interface BackwardsCompatibility {
  title: string;
  values: string[] | undefined;
}

export const getAllGenerators = (platform: Platform): BackwardsCompatibility[] => {
  return [
    { title: 'dbs.rechargeables', values: getRechargeableGeneratorsByPlatform(platform) },
    { title: 'dbs.primaryCells', values: getPrimaryCellGeneratorsByPlatform(platform) },
  ].filter(generators => !isEmpty(generators.values));
};

/**
 * Check if there are no generators available for current selections
 * @param {IDBS} document - DBS document
 * @param {string} platform - Active platform
 * @returns {boolean} - true if there are no generators available, false if some are available
 */
export const generatorsUnavailable = (document: Partial<IDBS>, platform: Platform): boolean => {
  const { leadRight: rightLead, leadLeft: leftLead, targetNucleus } = document;
  if (!rightLead || !leftLead || !targetNucleus) return true;
  const { rechargeables, primaryCells } = backwardsCompatibility({ rightLead, leftLead, platform });
  if (!rechargeables?.length && !primaryCells?.length) return true;
  return false;
};

/**
 * Find the generator that follows given generator chronologically
 * @param {IDBSGenerator} generator - The generator from which to start looking for the next generator
 * @param {IDBS} document - The DBS document where the next generator is looked for
 * @returns {IDBSGenerator | undefined} - The next generator if found, undefined if not
 */
export const findNextGenerator = (generator: IDBSGenerator, document: IDBS): IDBSGenerator | undefined => {
  const generatorsSorted = (document.generators ?? []).toSorted((a, b) =>
    sortPartialDate(b.generatorDate, a.generatorDate),
  );
  const currentIndex = generatorsSorted.findIndex(g => equals(generator, g));

  return currentIndex > 0 ? generatorsSorted[currentIndex - 1] : undefined;
};

/**
 * Returns endDate of the treatment or generatorNextChangeDate depending on whether the endDate or nextChangeDate is sooner
 * @param document - DBS document
 * @param index - index of the current generator being edited
 */
export const generatorDateHook = (document: IDBS, index: number): PartialDate | undefined => {
  const end = document.endDate;
  const change = document.generators?.[index]?.generatorNextChangeDate;
  if (document.hasEnded?.[0] === true && end && change && partialDateToValue(end) > partialDateToValue(change)) {
    return change;
  } else if (document.hasEnded?.[0] === true && end) {
    return end;
  } else if (change) {
    return change;
  } else return undefined;
};

/**
 * A function for deciding whether to render the latest generator or not.
 * Viimeisin generaattori siis näytetään joissain tapauksissa tämänhetkisenä generaattorina,
 * mutta on tapauksia jossa viimeisin generaattori ei ole tällä hetkellä käytössä
 * @param formData formData
 * @param editingGenerator boolean that tells us whether a generator is being edited or not
 */
export const renderLatestGeneratorOrNot = (
  formData: { onChange?: any; document: IDBS },
  editingGenerator: boolean | number,
): boolean => {
  if (
    formData.document.generators &&
    formData.document.generators.length > 0 &&
    editingGenerator !== true &&
    (formData.document.status === 'inProgress' ||
      (formData.document.status === 'ended' && formData.document.removed != 'yes') ||
      (formData.document.status === 'suspended' &&
        getLatestDiscontinuation(formData.document.discontinuationEvents ?? []) &&
        getLatestDiscontinuation(formData.document.discontinuationEvents ?? [])?.discontinuationType ===
          'generatorSwitchedOff'))
  ) {
    return true;
  } else {
    return false;
  }
};

/**
 * A function for deciding whether to render prvious generators or not
 * @param formData formData
 */
export const renderPreviousGeneratorsOrNot = (formData: { onChange?: any; document: IDBS }): boolean => {
  if (
    formData.document.generators &&
    (formData.document.generators.length > 1 ||
      (formData.document.status === 'suspended' &&
        formData.document.generators.length > 0 &&
        getLatestDiscontinuation(formData.document.discontinuationEvents ?? []) &&
        getLatestDiscontinuation(formData.document.discontinuationEvents ?? [])?.discontinuationType !=
          'generatorSwitchedOff') ||
      (formData.document.status === 'ended' &&
        formData.document.removed === 'yes' &&
        formData.document.generators.length > 0))
  ) {
    return true;
  } else {
    return false;
  }
};

/**
 * Check if the LFP registration program specific for Medtronic Percept PC exists in the setting
 * @param {string} setting Setting to be checked
 * @returns {boolean} True if exists, false if not
 */
export const hasLfpRegistration = (setting: IDBSSetting | undefined | null): boolean =>
  setting && Array.isArray(setting.programs)
    ? setting.programs.filter(program => program.lfpRegistration === true).length > 0
    : false;

interface IOwnProps {
  formData: IFormData<IDocumentWithTypeV2<'dbs'>>;
}
